import * as React from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/material/styles/useTheme';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import {
    useNavigate,
    useOutletContext
} from 'react-router-dom';
import NavBar from 'ui/components/classes-components/NavBar';
import { IOutletContext } from 'ui/components/Dashboard';
import { OrganizationReportSummary } from 'models/IStats';
import { useSession } from 'auth/AuthProvider';
import OrganizationReportCreationModal from 'ui/components/reports-components/OrganizationReportCreationModal';
import ReportsList from 'ui/components/shared/ReportsList';
import DeleteReportDialog from 'ui/components/shared/DeleteReportDialog';
import { useReportsList } from 'hooks/useReportsList';
import { useReportDeletion } from 'hooks/useReportDeletion';

export default function OrganizationReports() {
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('sm'));
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = React.useState(false);
    const session = useSession();
    
    const {
        setBgcolor,
        setTopBarColorLight
    } = useOutletContext<IOutletContext>();
    
    React.useEffect(() => {
        setBgcolor('background.paper');
        setTopBarColorLight("background.paper");
    }, [setBgcolor, setTopBarColorLight]);

    // Redirect teachers away from organization reports
    React.useEffect(() => {
        if (session.role !== 'manager') {
            navigate('/reports', { replace: true });
        }
    }, [session.role, navigate]);

    // Use refactored reports list hook
    const { groupedReports, isLoading, error, navigateToReport } = useReportsList<OrganizationReportSummary>({
        apiEndpoint: '/organization-reports',
        queryKey: 'organization_reports',
        reportUrlPrefix: '/organization_stat/',
        enabled: session.role === 'manager' && !!session.organization_id
    });

    // Use refactored deletion hook
    const {
        deleteDialogOpen,
        isDeleting,
        handleDeleteClick,
        handleDeleteConfirm,
        handleDeleteCancel
    } = useReportDeletion({
        apiEndpoint: '/organization-reports',
        queryKeyToInvalidate: 'organization_reports'
    });
    
    // Create nodes map for NavBar
    const nodesMap = new Map<string, string>();
    if (session.organization_name) {
        nodesMap.set('organization-reports', session.organization_name);
    }

    const handleReportCreated = (reportId: string) => {
        setModalOpen(false);
        navigate(`/organization_stat/${reportId}`);
    };

    if (isLoading) {
        return (
            <Container maxWidth='lg' sx={{ pt: 2 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth='lg' sx={{ pt: 2 }}>
                <Alert severity="error">Failed to load reports</Alert>
            </Container>
        );
    }

    if (!session.organization_id) {
        return (
            <Container maxWidth='lg' sx={{ pt: 2 }}>
                <Alert severity="error">No organization found</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth='lg' sx={{ pt: 2 }}>
            <Box sx={{ px: matches ? 6 : 0 }}>
                <NavBar nodesMap={nodesMap} />
                <Box sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: "space-between",
                    mb: 1,
                    mt: 6,
                    px: 0
                }}>
                    <Typography variant="h5" fontWeight={500} fontSize={32}>
                        All School Reports
                    </Typography>
                    <Button
                        variant='contained'
                        sx={{ fontSize: 17, px: matches ? 3 : 1 }}
                        onClick={() => setModalOpen(true)}
                    >
                        Create report
                    </Button>
                </Box>

                {/* Reports List */}
                <ReportsList
                    groupedReports={groupedReports}
                    onReportClick={navigateToReport}
                    onDeleteClick={handleDeleteClick}
                    emptyMessage="No reports available for this organization. Create your first report to get started."
                />
            </Box>

            {/* Organization Report Creation Modal */}
            <OrganizationReportCreationModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onReportCreated={handleReportCreated}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteReportDialog
                open={deleteDialogOpen}
                onCancel={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                isDeleting={isDeleting}
            />
        </Container>
    );
}